import type { Organization } from '@hcengineering/contact'
import type { AttachedDoc, Class, Doc, Hyperlink, IntegrationKind, Mixin, Ref, Timestamp } from '@hcengineering/core'
import type { Asset, IntlString, Metadata, Plugin } from '@hcengineering/platform'
import { plugin } from '@hcengineering/platform'
import type { Handler, IntegrationType } from '@hcengineering/setting'
import type { Project } from '@hcengineering/tracker'
import type { AnyComponent } from '@hcengineering/ui'
import type { Action, ActionCategory, ViewAction, Viewlet } from '@hcengineering/view'

/**
 * @public
 */
export const quickbooksId = 'dztek-quickbooks' as Plugin

/**
 * @public
 */
export const quickbooksIntegrationKind = 'dztek-quickbooks' as IntegrationKind

/**
 * The QuickBooks Online company connected to the workspace, one document per
 * workspace. The OAuth token set is held by the quickbooks service.
 * @public
 */
export interface Connection extends Doc {
  realmId: string
  companyName: string
  environment: string
  connectedBy: string
  connectedAt: Timestamp
  lastSyncAt?: Timestamp
  lastSyncStatus?: string
  lastSyncError?: string
  cdcSince?: Timestamp
}

/**
 * An Organization that is a QuickBooks Customer.
 * @public
 */
export interface QboCustomer extends Organization {
  qboId: string
  syncToken: string
  displayName: string
  balance: number
  lastSyncAt: Timestamp
  invoices?: number
}

/**
 * A Project stamped with the code that appears on its invoices, mapped to a
 * QuickBooks Class or Customer:Job.
 * @public
 */
export interface ProjectCode extends Project {
  code: string
  qboClassId?: string
  qboCustomerId?: string
  billable: boolean
}

/**
 * @public
 */
export type InvoiceStatus = 'open' | 'paid' | 'overdue' | 'voided'

/**
 * A QuickBooks Invoice attached to the billed Organization.
 * @public
 */
export interface Invoice extends AttachedDoc {
  attachedTo: Ref<Organization>
  qboId: string
  syncToken: string
  docNumber: string
  txnDate: Timestamp
  dueDate?: Timestamp
  total: number
  balance: number
  status: InvoiceStatus
  projectCode?: string
  webUrl: Hyperlink
}

export default plugin(quickbooksId, {
  class: {
    Connection: '' as Ref<Class<Connection>>,
    Invoice: '' as Ref<Class<Invoice>>
  },
  mixin: {
    QboCustomer: '' as Ref<Mixin<QboCustomer>>,
    ProjectCode: '' as Ref<Mixin<ProjectCode>>
  },
  component: {
    Connect: '' as AnyComponent,
    ConnectionDescription: '' as AnyComponent,
    IconQuickbooks: '' as AnyComponent,
    Invoices: '' as AnyComponent,
    InvoicePresenter: '' as AnyComponent,
    InvoiceStatusPresenter: '' as AnyComponent,
    ProjectCodeEditor: '' as AnyComponent,
    QboCustomerBadge: '' as AnyComponent
  },
  integrationType: {
    Quickbooks: '' as Ref<IntegrationType>
  },
  handler: {
    Disconnect: '' as Handler
  },
  action: {
    SyncCustomers: '' as Ref<Action>,
    OpenInQuickbooks: '' as Ref<Action>,
    EditProjectCode: '' as Ref<Action<Doc, any>>
  },
  actionImpl: {
    SyncCustomers: '' as ViewAction,
    OpenInQuickbooks: '' as ViewAction
  },
  category: {
    Quickbooks: '' as Ref<ActionCategory>
  },
  viewlet: {
    InvoiceTable: '' as Ref<Viewlet>
  },
  metadata: {
    ServiceUrl: '' as Metadata<string>
  },
  string: {
    Quickbooks: '' as IntlString,
    Description: '' as IntlString,
    Connection: '' as IntlString,
    Invoice: '' as IntlString,
    Invoices: '' as IntlString,
    QboCustomer: '' as IntlString,
    ProjectCode: '' as IntlString,
    Code: '' as IntlString,
    QboId: '' as IntlString,
    SyncToken: '' as IntlString,
    DisplayName: '' as IntlString,
    Balance: '' as IntlString,
    LastSyncAt: '' as IntlString,
    DocNumber: '' as IntlString,
    TxnDate: '' as IntlString,
    DueDate: '' as IntlString,
    Total: '' as IntlString,
    Status: '' as IntlString,
    WebUrl: '' as IntlString,
    QboClassId: '' as IntlString,
    QboCustomerId: '' as IntlString,
    Billable: '' as IntlString,
    RealmId: '' as IntlString,
    CompanyName: '' as IntlString,
    Environment: '' as IntlString,
    ConnectedBy: '' as IntlString,
    ConnectedAt: '' as IntlString,
    LastSyncStatus: '' as IntlString,
    LastSyncError: '' as IntlString,
    SyncCustomers: '' as IntlString,
    SyncInvoices: '' as IntlString,
    OpenInQuickbooks: '' as IntlString,
    EditProjectCode: '' as IntlString
  },
  icon: {
    Quickbooks: '' as Asset,
    Invoice: '' as Asset
  }
})
